/* istanbul ignore file */
import React, { useContext, useState } from 'react';
import { DataTableContext } from '@/components/table';
import { Box, Flex, IconButton, TableCaption, Text } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { TableFooterJump } from './table-footer-jump';

export const TableFooterReduced = () => {
  const { tableProps: props } = useContext(DataTableContext);
  const { styles, pageInfoReduced, setPageInfoReduced } = props;

  const [isJumpOpen, setIsJumpOpen] = useState<boolean>(false);
  const [jumpToPage, setJumpToPage] = useState<string>('');

  function setPageReduced(newPage: number) {
    if (setPageInfoReduced && pageInfoReduced) {
      setPageInfoReduced({
        ...pageInfoReduced,
        page: newPage,
        nextPageEnabled: newPage < pageInfoReduced.totalPages,
      });
    }
  }

  if (!pageInfoReduced) return null;

  const { page, totalPages, numItemsPerPage, nextPageEnabled, totalRecords } = pageInfoReduced;
  const isJumpDisabled = totalPages <= 1;
  const startItem = (page - 1) * numItemsPerPage + 1;
  const endItem = Math.min(startItem + numItemsPerPage - 1, totalRecords);
  const showPagination = totalRecords > 0 && startItem <= totalRecords;

  return (
    <TableCaption css={styles?.footer} px={4} py={2} bg="gray.100" borderBottomRadius={'md'}>
      <Flex justify="space-between" align="center">
        <Flex align="center" gap={2}>
          <Text css={styles?.footerText} color="typography.secondary" fontSize="xs" fontWeight="normal">
            {showPagination ? `${startItem}-${endItem} of ${totalRecords}` : '0 of 0'}
          </Text>
        </Flex>

        <Flex align="center" gap={4}>
          <Text css={styles?.footerText} color="typography.secondary" fontSize="xs" fontWeight="normal">
            {page} of {totalPages ?? '—'}
          </Text>
          <Box position="relative">
            <Text
              css={styles?.footerText}
              color={isJumpDisabled ? 'icon.disabled' : 'typography.secondary'}
              fontSize="xs"
              fontWeight="semibold"
              cursor={isJumpDisabled ? 'not-allowed' : 'pointer'}
              onClick={() => {
                if (!isJumpDisabled) {
                  setIsJumpOpen((prev) => !prev);
                }
              }}
            >
              Jump
            </Text>

            <TableFooterJump
              isOpen={isJumpOpen}
              onClose={() => setIsJumpOpen(false)}
              onSubmit={(pageNum) => setPageReduced(pageNum)}
              totalPages={totalPages}
              jumpToPage={jumpToPage}
              setJumpToPage={setJumpToPage}
            />
          </Box>
          <IconButton
            w={8}
            h={8}
            minW={8}
            minH={8}
            disabled={page <= 1}
            variant={page <= 1 ? 'ghost' : 'outline'}
            aria-label="Previous Page"
            border="none"
            mr={1}
            onClick={() => setPageReduced(page - 1)}
          >
            <ChevronLeft
              style={{ width: '16px', height: '16px', strokeWidth: '4px' }}
              color="var(--chakra-colors-typography-tertiary)"
            />
          </IconButton>

          <IconButton
            w={8}
            h={8}
            minW={8}
            minH={8}
            disabled={!nextPageEnabled}
            variant={nextPageEnabled ? 'ghost' : 'outline'}
            aria-label="Next Page"
            border="none"
            onClick={() => setPageReduced(page + 1)}
          >
            <ChevronRight
              style={{ width: '16px', height: '16px', strokeWidth: '4px' }}
              color="var(--chakra-colors-typography-tertiary)"
            />
          </IconButton>
        </Flex>
      </Flex>
    </TableCaption>
  );
};
